import { norm } from '../core/math'
import type { Intent, InputSource } from './types'
import { ClassicAimHelper, emptyIntent } from './types'

/**
 * Desktop source (default "modern" scheme): WASD/arrows move, mouse aims,
 * left-click autofires, right-click/space uses magic, E interacts, Esc/P pause.
 * Movement and aim are fully decoupled.
 */
export class KeyboardMouseSource implements InputSource {
  private keys = new Set<string>()
  private mouseDown = false
  private mouseX = 0
  private mouseY = 0
  private magicQueued = false
  private interactQueued = false
  private overrideQueued = false
  private pauseQueued = false
  private classic = false
  private classicHelper = new ClassicAimHelper()
  private disposers: (() => void)[] = []

  constructor(
    private canvas: HTMLCanvasElement,
    /** screen (client) coords -> world coords, provided by the render layer */
    private screenToWorld: (sx: number, sy: number) => { x: number; y: number }
  ) {
    const kd = (e: KeyboardEvent) => {
      if (e.repeat) return
      this.keys.add(e.code)
      if (e.code === 'Space' || e.code === 'KeyQ') {
        this.magicQueued = true
        e.preventDefault()
      }
      if (e.code === 'KeyE') this.interactQueued = true
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') this.overrideQueued = true
      if (e.code === 'Escape' || e.code === 'KeyP') this.pauseQueued = true
    }
    const ku = (e: KeyboardEvent) => this.keys.delete(e.code)
    const md = (e: MouseEvent) => {
      if (e.button === 0) this.mouseDown = true
      if (e.button === 2) {
        this.magicQueued = true
        e.preventDefault()
      }
    }
    const mu = (e: MouseEvent) => {
      if (e.button === 0) this.mouseDown = false
    }
    const mm = (e: MouseEvent) => {
      this.mouseX = e.clientX
      this.mouseY = e.clientY
    }
    const ctx = (e: Event) => e.preventDefault()
    const blur = () => {
      this.keys.clear()
      this.mouseDown = false
    }
    window.addEventListener('keydown', kd)
    window.addEventListener('keyup', ku)
    window.addEventListener('mousedown', md)
    window.addEventListener('mouseup', mu)
    window.addEventListener('mousemove', mm)
    window.addEventListener('blur', blur)
    this.canvas.addEventListener('contextmenu', ctx)
    this.disposers.push(
      () => window.removeEventListener('keydown', kd),
      () => window.removeEventListener('keyup', ku),
      () => window.removeEventListener('mousedown', md),
      () => window.removeEventListener('mouseup', mu),
      () => window.removeEventListener('mousemove', mm),
      () => window.removeEventListener('blur', blur),
      () => this.canvas.removeEventListener('contextmenu', ctx)
    )
  }

  poll(px: number, py: number): Intent {
    const intent = emptyIntent()
    const k = this.keys
    let mx = 0
    let my = 0
    if (k.has('KeyW') || k.has('ArrowUp')) my -= 1
    if (k.has('KeyS') || k.has('ArrowDown')) my += 1
    if (k.has('KeyA') || k.has('ArrowLeft')) mx -= 1
    if (k.has('KeyD') || k.has('ArrowRight')) mx += 1
    intent.move = norm({ x: mx, y: my })

    const world = this.screenToWorld(this.mouseX, this.mouseY)
    intent.aim = norm({ x: world.x - px, y: world.y - py })
    if (intent.aim.x === 0 && intent.aim.y === 0) intent.aim = { x: 1, y: 0 }

    intent.fire = this.mouseDown || k.has('KeyF')
    intent.magic = this.magicQueued
    intent.interact = this.interactQueued
    intent.override = this.overrideQueued
    intent.pause = this.pauseQueued
    this.magicQueued = false
    this.interactQueued = false
    this.overrideQueued = false
    this.pauseQueued = false

    return this.classic ? this.classicHelper.apply(intent) : intent
  }

  setClassicMode(on: boolean): void {
    this.classic = on
  }

  clearQueued(): void {
    this.magicQueued = false
    this.interactQueued = false
    this.overrideQueued = false
    this.pauseQueued = false
  }

  destroy(): void {
    for (const d of this.disposers) d()
    this.disposers = []
  }
}
